import { useEffect, useMemo, useState } from "react";
import {
  FleetDriverCreateDialog,
  FleetDriverEditDialog,
  FleetVehicleEditDialog,
} from "../components/FleetEditDialogs";
import {
  fetchTransporterFleetStatus,
  type TransporterFleetDriverDto,
  type TransporterFleetVehicleDto,
} from "../lib/fetch-transporter-delivery-ops";
import {
  daysUntilIsoDate,
  medCredentialLabel,
  medCredentialTone,
} from "../lib/med-credential-badge";
import { useTransporterWorkspace } from "../workspace/transporter-workspace-context";

function toneClasses(tone: ReturnType<typeof medCredentialTone>): string {
  switch (tone) {
    case "expired":
      return "text-red";
    case "critical":
      return "text-amber";
    case "warn":
      return "text-amber";
    default:
      return "text-gray";
  }
}

function MedExpiryCell({ isoDate }: { isoDate: string }) {
  const days = daysUntilIsoDate(isoDate);
  const tone = medCredentialTone(days);
  return (
    <div className="flex flex-col">
      <span className="font-mono text-xs text-gray">{isoDate}</span>
      <span className={`text-xs ${toneClasses(tone)}`}>{medCredentialLabel(days)}</span>
    </div>
  );
}

function CheckMark({ ok }: { ok: boolean }) {
  return ok ? (
    <span className="text-green" aria-label="yes">
      ✓
    </span>
  ) : (
    <span className="text-gray-light" aria-label="no">
      —
    </span>
  );
}

function VehicleRow({
  v,
  onEdit,
}: {
  v: TransporterFleetVehicleDto;
  onEdit: () => void;
}) {
  return (
    <tr>
      <td className="text-sm text-dark">
        {v.year} {v.make} {v.model}
        <span className="ml-2 text-gray">({v.color})</span>
      </td>
      <td className="font-mono text-sm text-text">{v.licensePlate}</td>
      <td>
        <MedExpiryCell isoDate={v.registrationExpiresAt} />
      </td>
      <td>
        <MedExpiryCell isoDate={v.insuranceExpiresAt} />
      </td>
      <td className="text-center text-sm">
        <CheckMark ok={v.hasSecurityAlarm} />
      </td>
      <td className="text-center text-sm">
        <CheckMark ok={v.hasLockedOpaqueStorage} />
      </td>
      <td className="text-center text-sm">
        <CheckMark ok={v.hasVideoSurveillance} />
      </td>
      <td className="text-sm">
        {v.isActive ? (
          <span className="text-gray">Active</span>
        ) : (
          <span className="text-gray-light">Inactive</span>
        )}
      </td>
      <td className="text-right">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-lg border border-td-card-border bg-white px-2 py-1 text-xs font-medium text-td-ink hover:bg-td-page"
        >
          Edit
        </button>
      </td>
    </tr>
  );
}

function DriverRow({
  d,
  onEdit,
}: {
  d: TransporterFleetDriverDto;
  onEdit: () => void;
}) {
  return (
    <tr>
      <td className="text-sm font-medium text-[#333333]">{d.fullName}</td>
      <td>
        <MedExpiryCell isoDate={d.medLicenseExpiresAt} />
      </td>
      <td>
        <MedExpiryCell isoDate={d.coLicenseExpiresAt} />
      </td>
      <td className="text-sm">
        {d.onShift ? (
          <span className="text-green">On shift</span>
        ) : (
          <span className="text-gray">Off shift</span>
        )}
      </td>
      <td className="text-sm">
        {d.availableForDispatch ? (
          <span className="text-green">Available</span>
        ) : (
          <span className="text-gray">—</span>
        )}
      </td>
      <td className="text-sm">
        {d.isActive ? (
          <span className="text-gray">Active</span>
        ) : (
          <span className="text-gray-light">Inactive</span>
        )}
      </td>
      <td className="text-right">
        <button
          type="button"
          onClick={onEdit}
          className="rounded-lg border border-td-card-border bg-white px-2 py-1 text-xs font-medium text-td-ink hover:bg-td-page"
        >
          Edit
        </button>
      </td>
    </tr>
  );
}

function sortDriversForTable(drivers: TransporterFleetDriverDto[]): TransporterFleetDriverDto[] {
  return [...drivers].sort((a, b) => {
    if (a.onShift !== b.onShift) return a.onShift ? -1 : 1;
    if (a.availableForDispatch !== b.availableForDispatch) return a.availableForDispatch ? -1 : 1;
    return a.fullName.localeCompare(b.fullName);
  });
}

export function FleetScreen() {
  const { transporterId, getToken, fleetRefreshNonce } = useTransporterWorkspace();
  const [drivers, setDrivers] = useState<TransporterFleetDriverDto[] | null>(null);
  const [vehicles, setVehicles] = useState<TransporterFleetVehicleDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [editDriver, setEditDriver] = useState<TransporterFleetDriverDto | null>(null);
  const [createDriverOpen, setCreateDriverOpen] = useState(false);
  const [editVehicle, setEditVehicle] = useState<TransporterFleetVehicleDto | null>(null);

  useEffect(() => {
    if (!transporterId) {
      setDrivers(null);
      setVehicles(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const r = await fetchTransporterFleetStatus(getToken, transporterId);
        if (!cancelled) {
          setDrivers(r.drivers);
          setVehicles(r.vehicles ?? []);
        }
      } catch (e) {
        if (!cancelled) {
          setDrivers(null);
          setVehicles(null);
          setError(e instanceof Error ? e.message : "fleet_load_failed");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [transporterId, getToken, refreshNonce, fleetRefreshNonce]);

  const sortedDrivers = useMemo(() => (drivers ? sortDriversForTable(drivers) : []), [drivers]);

  if (!transporterId) {
    return (
      <section className="space-y-2">
        <p className="text-sm text-gray">
          Choose a transporter in the scope panel (platform admin) or sign in with a dispatcher account linked to a
          transporter.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-gray">
            Drivers (shift, dispatch pool, credentials) and vehicles (registration, insurance, and compliance
            checklist).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRefreshNonce((n) => n + 1)}
          disabled={loading}
          className="btn-secondary px-3 text-text transition-colors duration-150 hover:border-green-dark hover:bg-green-pale disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      {error ? <p className="text-sm text-red">Could not load fleet. Try refreshing.</p> : null}
      {drivers !== null && !error ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray">Drivers</h2>
            <button
              type="button"
              onClick={() => setCreateDriverOpen(true)}
              disabled={loading}
              className="rounded-lg border border-td-card-border bg-white px-3 py-1.5 text-xs font-medium text-td-ink hover:bg-td-page disabled:opacity-50"
            >
              Add driver
            </button>
          </div>
          {sortedDrivers.length > 0 ? (
            <div className="td-table-wrap">
              <table className="td-data-table min-w-[42rem]">
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th>MED license</th>
                    <th>CO license</th>
                    <th>Shift</th>
                    <th>Dispatch pool</th>
                    <th>Record</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDrivers.map((d) => (
                    <DriverRow key={d.driverId} d={d} onEdit={() => setEditDriver(d)} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray">No drivers for this transporter yet.</p>
          )}
        </div>
      ) : null}
      {vehicles && vehicles.length > 0 ? (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray">Vehicles</h2>
          <p className="text-xs text-gray">
            Registration and insurance use the same 14-day / 3-day / expired ladder as driver MED credentials. Alarm,
            locked storage, and video are Colorado MED checklist fields.
          </p>
          <div className="td-table-wrap">
            <table className="td-data-table min-w-[56rem]">
              <thead>
                <tr>
                  <th>Vehicle</th>
                  <th>Plate</th>
                  <th>Registration</th>
                  <th>Insurance</th>
                  <th className="text-center">Alarm</th>
                  <th className="text-center">Locked storage</th>
                  <th className="text-center">Video</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <VehicleRow key={v.vehicleId} v={v} onEdit={() => setEditVehicle(v)} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      {drivers !== null && vehicles !== null && vehicles.length === 0 && !error ? (
        <p className="text-sm text-gray">No vehicles registered for this transporter.</p>
      ) : null}
      {!drivers && !error ? <p className="text-sm text-gray">Loading…</p> : null}

      {transporterId ? (
        <>
          <FleetDriverCreateDialog
            open={createDriverOpen}
            transporterId={transporterId}
            getToken={getToken}
            onClose={() => setCreateDriverOpen(false)}
            onSaved={() => setRefreshNonce((n) => n + 1)}
          />
          <FleetDriverEditDialog
            open={editDriver != null}
            driver={editDriver}
            transporterId={transporterId}
            getToken={getToken}
            onClose={() => setEditDriver(null)}
            onSaved={() => setRefreshNonce((n) => n + 1)}
          />
          <FleetVehicleEditDialog
            open={editVehicle != null}
            vehicle={editVehicle}
            transporterId={transporterId}
            getToken={getToken}
            onClose={() => setEditVehicle(null)}
            onSaved={() => setRefreshNonce((n) => n + 1)}
          />
        </>
      ) : null}
    </section>
  );
}
